import { Services } from './services/Services.js';
import { SERVICE_PROVIDER_CTORS } from './ServiceProviderRegistry.js';

class ServicesProvider {
    constructor(data) {
        this.programOwnerIds = data.programOwnerIds;
        this.name = data.name;
        this.email = data.email;
        this.phone = data.phone;
        this.onboardingCompleted = data.onboardingCompleted;
        this.serviceAccess = data.serviceAccess;
        this.onboardingCode = data.onboardingCode;
        this.onboardingPage = data.onboardingPage;
        this.invitedEmails = data.invitedEmails;
        this.primaryContact = data.primaryContact;
        this.id = data.id;
        this.dateCreated = data.dateCreated;
        this.dateUpdated = data.dateUpdated;
        this.config = data.config;
        this.uniqueReadableKey = data.uniqueReadableKey;
        // Store the full original data (may include service-specific fields like analyticsBoardUrl, physicalAddress, etc.)
        // If data already has _originalData (from a previous conversion), use that instead
        this._originalData = data._originalData || { ...data };
    }
    toObject() {
        // Destructure to exclude _originalData from the spread
        const { _originalData, ...currentInstanceData } = this;
        // Return the full original data merged with current instance properties.
        // This ensures service-specific fields are preserved when converting to sub-classes,
        // while also preserving any runtime modifications to properties like config.
        // Using spread of currentInstanceData handles both base class and subclass properties.
        return {
            ..._originalData,
            ...currentInstanceData,
        };
    }
    hasService(service) {
        return this?.config?.[service]?.supported === true;
    }
    /**
     * Get a registered subclass by identifier from ServiceProviderRegistry
     * @param identifier - The service identifier
     * @returns The subclass constructor
     */
    static getSubclass(identifier) {
        const SubClass = SERVICE_PROVIDER_CTORS[identifier];
        if (!SubClass) {
            throw new Error(`Unknown subclass: ${identifier}. Make sure the service provider class is imported and registered.`);
        }
        return SubClass;
    }
    /**
     * Register PayableServicesProvider class
     */
    static registerPayableSubclass(SubClass) {
        this.payableSubclassRegistry = SubClass;
    }
    toServicesProviderProgramOwner() {
        if (!this.hasService(Services.PROGRAM_OWNER)) {
            throw new Error('This services provider cannot be a program owner');
        }
        const Class = ServicesProvider.getSubclass(Services.PROGRAM_OWNER);
        return new Class(this.toObject());
    }
    toServicesProviderDomains() {
        if (!this.hasService(Services.DOMAINS)) {
            throw new Error('This services provider does not support domains service');
        }
        const Class = ServicesProvider.getSubclass(Services.DOMAINS);
        return new Class(this.toObject());
    }
    toServicesProviderSale() {
        if (!this.hasService(Services.SALE)) {
            throw new Error('This services provider does not support sale service');
        }
        const Class = ServicesProvider.getSubclass(Services.SALE);
        return new Class(this.toObject());
    }
    toServicesProviderDelivery() {
        if (!this.hasService(Services.DELIVERY)) {
            throw new Error('This services provider does not support delivery service');
        }
        const Class = ServicesProvider.getSubclass(Services.DELIVERY);
        return new Class(this.toObject());
    }
    toServicesProviderPickup() {
        if (!this.hasService(Services.PICKUP)) {
            throw new Error('This services provider does not support pickup service');
        }
        const Class = ServicesProvider.getSubclass(Services.PICKUP);
        return new Class(this.toObject());
    }
    toServicesProviderAssembly() {
        if (!this.hasService(Services.ASSEMBLY)) {
            throw new Error('This services provider does not support assembly service');
        }
        const Class = ServicesProvider.getSubclass(Services.ASSEMBLY);
        return new Class(this.toObject());
    }
    toServicesProviderInspection() {
        if (!this.hasService(Services.INSPECTION)) {
            throw new Error('This services provider does not support inspection service');
        }
        const Class = ServicesProvider.getSubclass(Services.INSPECTION);
        return new Class(this.toObject());
    }
    toServicesProviderTitling() {
        if (!this.hasService(Services.TITLING)) {
            throw new Error('This services provider does not support titling service');
        }
        const Class = ServicesProvider.getSubclass(Services.TITLING);
        return new Class(this.toObject());
    }
    toServicesProviderRegistration() {
        if (!this.hasService(Services.REGISTRATION)) {
            throw new Error('This services provider does not support registration service');
        }
        const Class = ServicesProvider.getSubclass(Services.REGISTRATION);
        return new Class(this.toObject());
    }
    toServicesProviderFinancing() {
        if (!this.hasService(Services.FINANCING)) {
            throw new Error('This services provider does not support financing service');
        }
        const Class = ServicesProvider.getSubclass(Services.FINANCING);
        return new Class(this.toObject());
    }
    toServicesProviderFinancingBrokerage() {
        if (!this.hasService(Services.FINANCING_BROKERAGE)) {
            throw new Error('This services provider does not support financing brokerage service');
        }
        const Class = ServicesProvider.getSubclass(Services.FINANCING_BROKERAGE);
        return new Class(this.toObject());
    }
    toServicesProviderLicensure() {
        if (!this.hasService(Services.LICENSURE)) {
            throw new Error('This services provider does not support licensure service');
        }
        const Class = ServicesProvider.getSubclass(Services.LICENSURE);
        return new Class(this.toObject());
    }
    toServicesProviderFinancingBrokerageSubvention() {
        if (!this.hasService(Services.FINANCING_BROKERAGE_SUBVENTION)) {
            throw new Error('This services provider does not support financing brokerage subvention service');
        }
        const Class = ServicesProvider.getSubclass(Services.FINANCING_BROKERAGE_SUBVENTION);
        return new Class(this.toObject());
    }
    toServicesProviderTariff() {
        if (!this.hasService(Services.TARIFF)) {
            throw new Error('This services provider does not support tariff service');
        }
        const Class = ServicesProvider.getSubclass(Services.TARIFF);
        return new Class(this.toObject());
    }
    toServicesProviderInventory() {
        if (!this.hasService(Services.INVENTORY)) {
            throw new Error('This services provider does not support inventory service');
        }
        const Class = ServicesProvider.getSubclass(Services.INVENTORY);
        return new Class(this.toObject());
    }
    toServicesProviderFreight() {
        if (!this.hasService(Services.FREIGHT)) {
            throw new Error('This services provider does not support freight service');
        }
        const Class = ServicesProvider.getSubclass(Services.FREIGHT);
        return new Class(this.toObject());
    }
    toServicesProviderProcessingFees() {
        if (!this.hasService(Services.PROCESSING_FEES)) {
            throw new Error('This services provider does not support processing fees service');
        }
        const Class = ServicesProvider.getSubclass(Services.PROCESSING_FEES);
        return new Class(this.toObject());
    }
    toServicesProviderCoverageFulfillment() {
        if (!this.hasService(Services.COVERAGE_FULFILLMENT)) {
            throw new Error('This services provider does not support coverage fulfillment service');
        }
        const Class = ServicesProvider.getSubclass(Services.COVERAGE_FULFILLMENT);
        return new Class(this.toObject());
    }
    toServicesProviderCoverageTax() {
        if (!this.hasService(Services.COVERAGE_TAX)) {
            throw new Error('This services provider does not support coverage tax service');
        }
        const Class = ServicesProvider.getSubclass(Services.COVERAGE_TAX);
        return new Class(this.toObject());
    }
    toServicesProviderExternalTax() {
        if (!this.hasService(Services.EXTERNAL_TAX)) {
            throw new Error('This services provider does not support external tax service');
        }
        const Class = ServicesProvider.getSubclass(Services.EXTERNAL_TAX);
        return new Class(this.toObject());
    }
    toServicesProviderExternalFees() {
        if (!this.hasService(Services.EXTERNAL_FEES)) {
            throw new Error('This services provider does not support external fees service');
        }
        const Class = ServicesProvider.getSubclass(Services.EXTERNAL_FEES);
        return new Class(this.toObject());
    }
    toServicesProviderOriginator() {
        if (!this.hasService(Services.ORIGINATOR)) {
            throw new Error('This services provider does not support originator service');
        }
        const Class = ServicesProvider.getSubclass(Services.ORIGINATOR);
        return new Class(this.toObject());
    }
    toServicesProviderTempTag() {
        if (!this.hasService(Services.TEMP_TAG)) {
            throw new Error('This services provider does not support temp tag service');
        }
        const Class = ServicesProvider.getSubclass(Services.TEMP_TAG);
        return new Class(this.toObject());
    }
    toServicesProviderPlatform() {
        if (!this.hasService(Services.PLATFORM)) {
            throw new Error('This services provider does not support platform service');
        }
        const Class = ServicesProvider.getSubclass(Services.PLATFORM);
        return new Class(this.toObject());
    }
    toServicesProviderFinancingBrokeragePlatform() {
        if (!this.hasService(Services.FINANCING_BROKERAGE_PLATFORM)) {
            throw new Error('This services provider does not support financing brokerage platform service');
        }
        const Class = ServicesProvider.getSubclass(Services.FINANCING_BROKERAGE_PLATFORM);
        return new Class(this.toObject());
    }
    toServicesProviderInternalFees() {
        if (!this.hasService(Services.INTERNAL_FEES)) {
            throw new Error('This services provider does not support internal fees service');
        }
        const Class = ServicesProvider.getSubclass(Services.INTERNAL_FEES);
        return new Class(this.toObject());
    }
    toServicesProviderCoverageVendor() {
        if (!this.hasService(Services.COVERAGE_VENDOR)) {
            throw new Error('This services provider does not support coverage vendor service');
        }
        const Class = ServicesProvider.getSubclass(Services.COVERAGE_VENDOR);
        return new Class(this.toObject());
    }
    toServicesProviderCoveragePlatform() {
        if (!this.hasService(Services.COVERAGE_PLATFORM)) {
            throw new Error('This services provider does not support coverage platform service');
        }
        const Class = ServicesProvider.getSubclass(Services.COVERAGE_PLATFORM);
        return new Class(this.toObject());
    }
    toServicesProviderForService(service) {
        switch (service) {
            case Services.PROGRAM_OWNER:
                return this.toServicesProviderProgramOwner();
            case Services.DOMAINS:
                return this.toServicesProviderDomains();
            case Services.SALE:
                return this.toServicesProviderSale();
            case Services.DELIVERY:
                return this.toServicesProviderDelivery();
            case Services.PICKUP:
                return this.toServicesProviderPickup();
            case Services.ASSEMBLY:
                return this.toServicesProviderAssembly();
            case Services.INSPECTION:
                return this.toServicesProviderInspection();
            case Services.TITLING:
                return this.toServicesProviderTitling();
            case Services.REGISTRATION:
                return this.toServicesProviderRegistration();
            case Services.FINANCING:
                return this.toServicesProviderFinancing();
            case Services.FINANCING_BROKERAGE:
                return this.toServicesProviderFinancingBrokerage();
            case Services.LICENSURE:
                return this.toServicesProviderLicensure();
            case Services.TARIFF:
                return this.toServicesProviderTariff();
            case Services.INVENTORY:
                return this.toServicesProviderInventory();
            case Services.FREIGHT:
                return this.toServicesProviderFreight();
            case Services.PROCESSING_FEES:
                return this.toServicesProviderProcessingFees();
            case Services.COVERAGE_FULFILLMENT:
                return this.toServicesProviderCoverageFulfillment();
            case Services.COVERAGE_TAX:
                return this.toServicesProviderCoverageTax();
            case Services.EXTERNAL_TAX:
                return this.toServicesProviderExternalTax();
            case Services.EXTERNAL_FEES:
                return this.toServicesProviderExternalFees();
            case Services.ORIGINATOR:
                return this.toServicesProviderOriginator();
            case Services.TEMP_TAG:
                return this.toServicesProviderTempTag();
            case Services.PLATFORM:
                return this.toServicesProviderPlatform();
            case Services.FINANCING_BROKERAGE_PLATFORM:
                return this.toServicesProviderFinancingBrokeragePlatform();
            case Services.INTERNAL_FEES:
                return this.toServicesProviderInternalFees();
            case Services.COVERAGE_VENDOR:
                return this.toServicesProviderCoverageVendor();
            case Services.COVERAGE_PLATFORM:
                return this.toServicesProviderCoveragePlatform();
            case Services.FINANCING_BROKERAGE_SUBVENTION:
                return this.toServicesProviderFinancingBrokerageSubvention();
            case Services.BDC:
                return this;
            default:
                throw new Error(`Unsupported service: ${service}`);
        }
    }
    toPayableServicesProvider() {
        if (!ServicesProvider.payableSubclassRegistry) {
            throw new Error('PayableServicesProvider not registered');
        }
        return new ServicesProvider.payableSubclassRegistry(this.toObject());
    }
}
// Registry for PayableServicesProvider
ServicesProvider.payableSubclassRegistry = null;

export { ServicesProvider };
//# sourceMappingURL=ServicesProvider.js.map
